import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import { useState } from "react";
import { type GestureResponderEvent, Platform, StyleSheet } from "react-native";

import { Pressable, Text } from "../../../../mads";
import { ThumbsDown } from "../../../../mads/icons/ThumbsDown";
import { useI18n } from "../../../../shared/I18n";

export type DislikeButtonProps = {
  onPressDislike: () => void;
  jobTitle?: string;
  testID?: string;
  appearanceMode?: "default" | "utility";
  isDisliked?: boolean;
  disabled?: boolean;
};

export function DislikeButton({
  onPressDislike,
  jobTitle,
  testID,
  appearanceMode = "default",
  isDisliked = false,
  disabled = false,
}: DislikeButtonProps) {
  const i18n = useI18n();
  // Hover is web/mouse only (onHoverIn/onHoverOut never fire on native), so
  // isHovered stays false on iOS/Android and the hover style is a no-op there.
  const [isHovered, setIsHovered] = useState(false);
  const accessibilityLabel = isDisliked
    ? jobTitle
      ? i18n.gettext("Undo not interested, {0}", [jobTitle])
      : i18n.gettext("Undo")
    : jobTitle
      ? i18n.gettext("Not Interested, {0}", [jobTitle])
      : i18n.gettext("Not Interested");
  const accessibilityHint = isDisliked
    ? i18n.gettext("Restore this job to the results")
    : jobTitle
      ? i18n.gettext("{0} group. Tap to dislike the job", [jobTitle])
      : i18n.gettext("Job actions group. Tap to dislike the job");

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      sx={({ pressed }) => [
        styles.dislikeButton,
        appearanceMode === "utility" && styles.utilityButton,
        appearanceMode === "utility" && isDisliked && styles.utilityButtonSelected,
        appearanceMode === "utility" &&
          isHovered &&
          !pressed &&
          !isDisliked &&
          !disabled &&
          styles.utilityButtonHovered,
        (pressed || isDisliked) && styles.dislikeButtonPressed,
        appearanceMode === "utility" && (pressed || isDisliked) && styles.utilityButtonPressed,
        disabled && styles.dislikeButtonDisabled,
      ]}
      // `onHoverIn`/`onHoverOut` are real props on mads' Pressable but aren't typed
      // on the strict/RSD Pressable (jobcard is codegenned to jobcard-strict), so
      // spread them to type-check as both variants. Same pattern as
      // PickerCombobox.web.tsx.
      {...{
        onHoverIn: () => setIsHovered(true),
        onHoverOut: () => setIsHovered(false),
      }}
      onPress={(event?: GestureResponderEvent) => {
        event?.stopPropagation();
        event?.preventDefault();
        onPressDislike();
      }}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled, selected: isDisliked }}
      role="button"
      accessibilityHint={accessibilityHint}
      {...Platform.select({
        web: {
          "aria-pressed": isDisliked,
        },
      })}
      // Keep the icon and button as one screen-reader control; otherwise the SVG can appear
      // as its own focus target and split the label from the action (same pattern as SaveButton).
      accessible={true}
      testID={testID}
    >
      {isDisliked ? (
        <ThumbsDown fill={colors.foregroundStrong} />
      ) : (
        <ThumbsDown variant="stroke" fill={colors.foregroundStrong} />
      )}
      {appearanceMode === "utility" && isDisliked && (
        <Text sx={styles.hiddenLabel}>{i18n.gettext("Job hidden")}</Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  dislikeButton: {
    padding: 4,
    borderRadius: spacing.s6,
    justifyContent: "center",
    alignItems: "center",
  },
  utilityButton: {
    width: 44,
    height: 44,
    padding: 10,
    borderRadius: borderRadius.md,
    backgroundColor: colors.backgroundActionWeak,
    flexDirection: "row",
  },
  utilityButtonSelected: {
    width: "auto",
    paddingHorizontal: 16,
  },
  utilityButtonHovered: {
    backgroundColor: colors.backgroundActionWeakHover,
  },
  dislikeButtonPressed: {
    backgroundColor: colors.backgroundStrong,
  },
  utilityButtonPressed: {
    backgroundColor: colors.backgroundActionWeakActive,
  },
  dislikeButtonDisabled: {
    opacity: 0.5,
  },
  hiddenLabel: {
    marginLeft: spacing.s2,
    color: colors.foregroundStrong,
    fontWeight: "bold",
  },
});
